import {
    FieldErrors,
    UseFormClearErrors,
    UseFormRegister,
    UseFormSetError,
    UseFormSetValue,
    UseFormWatch,
} from 'react-hook-form';
import { H1, Link, P, StyleFontSkin, Typography, tokensRaw } from '@vp/swan';
import styled from 'styled-components';
import { DS_REQUEST_BRIEF_CREATE_URL, FormattedReactMessage, __, __url } from '@99designs/i18n';
import { getRequestType } from '../../Submit/requestDataFromBrief';
import { FormInput } from '../BriefForm';
import { Field } from '../Field';
import { FieldSet } from '../Field/FieldDisplay';
import { isFieldEmpty } from '../Field/helpers';
import { useIsGroupHidden } from '../Field/useIsGroupHidden';
import decisionFromBrief from '../FormFields/ClientConfiguredFields/ProductOptions/decisionFromBrief';
import { useIsEditMode, useIsReviewMode } from '../ViewContext';
import {
    FieldFragment,
    Field_GridChoiceField_Fragment,
    StructuredBriefFragment,
} from '../brief.generated';
import {
    UNIMPLEMENTED_QUESTION_KEYS_EDITABLE,
    UNIMPLEMENTED_QUESTION_KEYS_READONLY,
} from '../questions';
import { useGroupTitleWithOverrides } from './useGroupTitleWithOverrides';

export class FieldGroupData implements FieldGroupData {
    public static create({
        id,
        title,
        description,
        fields,
        isIntro,
    }: {
        id: string;
        title: string;
        description: string | null;
        fields: FieldFragment[];
        isIntro?: boolean;
    }) {
        return new FieldGroupData(id, title, description, fields, isIntro);
    }

    constructor(
        public id: string,
        public title: string,
        public description: string | null,
        public fields: FieldFragment[],
        public isIntro = false
    ) {}

    shouldShowIntroGroupHeading = (isReviewMode = false) => {
        return isReviewMode || !this.isIntro;
    };
}

type FieldGroupProps = {
    group: FieldGroupData;
    errors: FieldErrors<FormInput>;
    register: UseFormRegister<FormInput>;
    setValue: UseFormSetValue<FormInput>;
    watch: UseFormWatch<FormInput>;
    clearErrors: UseFormClearErrors<FormInput>;
    setError: UseFormSetError<FormInput>;
    titleFontSkin?: StyleFontSkin;
    isIntro?: boolean;
    esmpvId?: string;
    locale?: string;
};

export const Legend = styled.legend`
    font-size: 1.5rem;
    font-weight: 700;
    padding: ${tokensRaw.SwanSemSpace4} 0 ${tokensRaw.SwanSemSpace4} 0;
`;

export function FieldGroup({
    group,
    register,
    errors,
    setValue,
    watch,
    clearErrors,
    setError,
    titleFontSkin = 'title-headline',
    isIntro = false,
    esmpvId,
    locale,
}: FieldGroupProps) {
    const isReviewMode = useIsReviewMode();
    const isEditMode = useIsEditMode();
    const isGroupHidden = useIsGroupHidden(group);

    const title = useGroupTitleWithOverrides(group);

    if (isGroupHidden) {
        return null;
    }

    // Edit mode shouldn't watch the request type nor MPV ID decision as they aren't editable.

    const isDesignLive =
        (isEditMode
            ? (
                  group.fields.filter((field) => field.id === 'requestType')[0] as
                      | Field_GridChoiceField_Fragment
                      | undefined
              )?.choiceValue
            : watch('requestType', getRequestType(group.fields))) === 'DesignLive';
    const hasDecisionQuestion = group.fields.some((field) => field.id === 'productOptionsDecision');
    const hasProductOptions =
        hasDecisionQuestion &&
        watch('productOptionsDecision', decisionFromBrief(group.fields)) === 'CUSTOMER';
    const hasDesignPhysicalProduct = group.fields.some(
        (field) => field.id === 'physicalProduct' && field.groupId === 'productConfiguration'
    );
    const mpvId = isEditMode
        ? group.fields.filter((field) => field.id === 'physicalProduct')[0]?.id
        : watch('physicalProduct', '');

    let requestBriefCreateUrl: URL | null = null;
    if (esmpvId) {
        requestBriefCreateUrl = new URL(DS_REQUEST_BRIEF_CREATE_URL, window.location.origin);

        requestBriefCreateUrl.searchParams.set('esmpvId', esmpvId);
        requestBriefCreateUrl.searchParams.set('ib', 'false');

        if (locale) {
            requestBriefCreateUrl.searchParams.set('locale', locale);
        }

        if (isDesignLive) {
            requestBriefCreateUrl.searchParams.set('requestType', 'DesignLive');
        }

        if (mpvId) {
            requestBriefCreateUrl.searchParams.set('mpvId', mpvId);
        }
    }

    return (
        <FieldSet data-testid={`field-group-${group.id}`}>
            {group.shouldShowIntroGroupHeading(isReviewMode) && (
                <H1 fontSkin={titleFontSkin} id={group.id} mb={isReviewMode ? 5 : 3}>
                    {title}
                </H1>
            )}
            {group.description && (
                <P fontSkin="body-standard" mt="0" mb="4">
                    {group.description}
                </P>
            )}
            {group.fields.map((f) => {
                // These checks help "related fields" disappear immediately before waiting for mutations to complete.

                if (!isDesignLive && f.id === 'appointmentTime') {
                    return null;
                } else if (
                    !hasDesignPhysicalProduct &&
                    hasDecisionQuestion &&
                    !hasProductOptions &&
                    f.id === 'productOptions'
                ) {
                    return null;
                }

                // Those groups also need to have consistent padding even when inputs change.

                const isConditionField =
                    f.id === 'requestType' || f.id === 'productOptionsDecision';
                const isMultiFieldGroup = group.fields.length > 1 || isConditionField;

                return (
                    <Field
                        key={`form-field-${f.id}`}
                        register={register}
                        field={f}
                        error={errors[f.id]}
                        setValue={setValue}
                        clearErrors={clearErrors}
                        setError={setError}
                        isMultiFieldGroup={isMultiFieldGroup}
                        isIntro={isIntro}
                        isOmitRequiredIndicator={isConditionField}
                    />
                );
            })}
            {group.id === 'addAStartingFile' && requestBriefCreateUrl && (
                <Typography fontSkin="body-standard">
                    <FormattedReactMessage
                        message={__(
                            "Don't have a starting file? <Link>Start a brief from scratch</Link>",
                            {
                                _context:
                                    'This is the message for starting a brief from scratch when customer does not have a starting file',
                            }
                        )}
                        replacements={{
                            Link: (
                                <Link
                                    to={__url(
                                        requestBriefCreateUrl.pathname +
                                            requestBriefCreateUrl.search
                                    )}
                                />
                            ),
                        }}
                    />
                </Typography>
            )}
        </FieldSet>
    );
}

export enum FieldGroupVariant {
    EDITABLE,
    READONLY,
}

export function fieldGroups(
    brief: StructuredBriefFragment,
    variant: FieldGroupVariant = FieldGroupVariant.EDITABLE
) {
    const unimplementedQuestionKeys =
        variant === FieldGroupVariant.READONLY
            ? UNIMPLEMENTED_QUESTION_KEYS_READONLY
            : UNIMPLEMENTED_QUESTION_KEYS_EDITABLE;
    return brief.groups.map((group) => {
        const fields = brief.dynamicFields.filter(
            (field) => field.groupId === group.id && !unimplementedQuestionKeys.includes(field.id)
        );

        return new FieldGroupData(group.id, group.title, group.description, fields);
    });
}

export function isGroupEmpty(group: FieldGroupData): boolean {
    return group.fields.every((field) => isFieldEmpty(field));
}
